import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal, Crown } from "lucide-react";

const statusStyle = (s) => {
  if (s === "active") return "bg-[#ccff0c]/15 text-[#ccff0c] border-[#ccff0c]/30";
  if (s === "trialing") return "bg-secondary text-muted-foreground border-border";
  return "bg-red-500/15 text-red-300 border-red-500/25";
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // user object
  const [form, setForm] = useState({ status: "active", tier: "standard", days: "", isAdmin: false });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Admin only"));
  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      status: u.access?.status === "active" ? "active" : u.access?.status === "trialing" ? "trialing" : "none",
      tier: u.subscription_tier === "coaching" ? "coaching" : "standard",
      days: "",
      isAdmin: u.role === "admin",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        tier: form.tier,
        role: form.isAdmin ? "admin" : "user",
      };
      if (form.days && parseInt(form.days, 10) > 0) payload.days = parseInt(form.days, 10);
      await api.put(`/admin/users/${editing.id}/membership`, payload);
      toast.success("Membership updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update membership");
    } finally { setSaving(false); }
  };

  const durationPlaceholder = form.status === "trialing" ? "7" : (form.tier === "coaching" ? "30" : "30");

  return (
    <div className="mt-4 space-y-2" data-testid="admin-users-table">
      {users.map((u) => (
        <Card key={u.id} data-testid={`admin-user-${u.id}`} className="rounded-xl bg-card border-border p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{u.name}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <Badge className={`text-[10px] border ${statusStyle(u.access?.status)}`}>{u.access?.status || "none"}</Badge>
                {u.subscription_tier === "coaching" && (
                  <Badge className="text-[10px] bg-[#ccff0c] text-black flex items-center gap-0.5"><Crown className="h-2.5 w-2.5" /> Coaching</Badge>
                )}
                <Badge className="text-[10px] bg-secondary border border-border text-muted-foreground">{u.role}</Badge>
              </div>
            </div>
            <Button data-testid={`manage-user-${u.id}`} size="sm" onClick={() => openEdit(u)} className="bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40 flex-shrink-0">
              <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Manage
            </Button>
          </div>
        </Card>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-popover border-border max-w-[440px]">
          <DialogHeader><DialogTitle className="font-display">Manage membership</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4" data-testid="membership-editor">
              <div>
                <p className="font-semibold">{editing.name}</p>
                <p className="text-xs text-muted-foreground">{editing.email}</p>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Membership status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="membership-status" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border text-foreground">
                    <SelectItem value="active">Active (paid)</SelectItem>
                    <SelectItem value="trialing">Trial</SelectItem>
                    <SelectItem value="none">None (revoke access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.status === "active" && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Tier</Label>
                  <Select value={form.tier} onValueChange={(v) => setForm((f) => ({ ...f, tier: v }))}>
                    <SelectTrigger data-testid="membership-tier" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border text-foreground">
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="coaching">1-on-1 Coaching</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.status !== "none" && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Duration (days) — leave blank for default ({durationPlaceholder})</Label>
                  <Input data-testid="membership-days" type="number" min="1" value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))} placeholder={durationPlaceholder} className="bg-card border-border h-11" />
                </div>
              )}

              <div className="flex items-center justify-between rounded-xl bg-card border border-border px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">Admin role</p>
                  <p className="text-[11px] text-muted-foreground">Grants full admin access</p>
                </div>
                <Switch data-testid="membership-admin-toggle" checked={form.isAdmin} onCheckedChange={(v) => setForm((f) => ({ ...f, isAdmin: v }))} />
              </div>

              <p className="text-[11px] text-muted-foreground leading-relaxed">Set tier to <span className="text-[#ccff0c]">1-on-1 Coaching</span> to unlock this member's Exclusive Workouts tab and their Coach Assigned shortcut on the dashboard.</p>

              <Button data-testid="save-membership" onClick={save} disabled={saving} className="w-full h-11 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
